import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, Loader2, CalendarX, MapPin, Clock } from 'lucide-react';

// Öffentliche Event-Einladung (Sommerfest, Gymfights …) — per WhatsApp/QR
// von einem Mitglied geteilt. Gast sagt mit Name + Handynummer zu.
const P1_RED = '#d1272e';
const API = process.env.REACT_APP_BACKEND_URL;

const WOCHENTAGE = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

function fmtDatum(iso) {
  try {
    const d = new Date(iso + 'T00:00:00');
    return `${WOCHENTAGE[d.getDay()]}., ${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`;
  } catch { return iso; }
}

export default function EventPage() {
  const { eventId, code } = useParams();
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '' });
  const [personen, setPersonen] = useState(1);
  const [honeypot, setHoneypot] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/public/event/${eventId}/${code}`);
      if (!res.ok) throw new Error();
      setInfo(await res.json());
    } catch {
      setInvalid(true);
    }
    setLoading(false);
  }, [eventId, code]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setError('');
    if (form.name.trim().length < 3 || !form.name.trim().includes(' ')) {
      setError('Bitte gib deinen Vor- und Nachnamen an.'); return;
    }
    if (form.phone.replace(/\D/g, '').length < 8) {
      setError('Bitte gib deine Handynummer an.'); return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/public/event/${eventId}/${code}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, personen, website: honeypot }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d?.detail || 'Das hat leider nicht geklappt.');
      setDone(d.message || 'Du stehst auf der Gästeliste!');
    } catch (e) {
      setError(e.message);
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: "'Manrope', sans-serif" }} data-testid="event-page">
      <div className="bg-white border-b border-gray-200 px-4 py-4 flex items-center justify-center">
        <img src="/logo-black.png" alt="P1 Performance Center" className="h-9 object-contain" />
      </div>

      <div className="max-w-md mx-auto px-4 py-6 pb-16">
        {loading ? (
          <div className="text-center py-24">
            <Loader2 className="w-8 h-8 animate-spin mx-auto" style={{ color: P1_RED }} />
          </div>
        ) : invalid ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center mt-8">
            <CalendarX className="w-10 h-10 mx-auto text-gray-300 mb-3" />
            <h1 className="text-lg font-bold">Einladung nicht mehr gültig</h1>
            <p className="text-sm text-gray-500 mt-2">Frag dein P1-Mitglied nach einer neuen Einladung.</p>
          </div>
        ) : done ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center mt-8" data-testid="event-done">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-3" style={{ color: '#16a34a' }} />
            <h1 className="text-xl font-extrabold">Zusage steht! {info.emoji}</h1>
            <p className="text-sm text-gray-600 mt-3">{done}</p>
            <p className="text-sm text-gray-500 mt-2">{fmtDatum(info.datum)}{info.zeit ? ` · ${info.zeit} Uhr` : ''}{info.ort ? ` · ${info.ort}` : ''}</p>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <div className="text-5xl mb-2">{info.emoji}</div>
              <h1 className="text-2xl font-extrabold tracking-tight">
                {info.vorname} lädt dich ein: {info.titel}
              </h1>
              <div className="flex items-center justify-center gap-4 mt-3 text-sm text-gray-600">
                <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{fmtDatum(info.datum)}{info.zeit ? ` · ${info.zeit} Uhr` : ''}</span>
                {info.ort && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{info.ort}</span>}
              </div>
              {info.beschreibung && <p className="text-sm text-gray-500 mt-3">{info.beschreibung}</p>}
            </div>

            {info.plaetze_frei <= 0 ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
                <p className="text-sm text-gray-500">Die Gästeplätze dieser Einladung sind schon vergeben — frag dein P1-Mitglied oder komm spontan vorbei.</p>
              </div>
            ) : (
              <>
                {error && <div className="mb-4 text-sm text-white rounded-xl px-4 py-3" style={{ backgroundColor: P1_RED }}>{error}</div>}
                <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4">
                  <div>
                    <label className="text-xs font-bold text-gray-500 uppercase">Vor- und Nachname</label>
                    <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="Max Mustermann"
                      className="mt-1 w-full border border-gray-300 rounded-xl px-4 py-3 text-base" />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-500 uppercase">Handynummer</label>
                    <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                      placeholder="0176 12345678" type="tel"
                      className="mt-1 w-full border border-gray-300 rounded-xl px-4 py-3 text-base" />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-500 uppercase">Wie viele kommt ihr?</label>
                    <div className="flex gap-2 mt-1">
                      {[1, 2, 3, 4].filter(n => n <= info.plaetze_frei).map(n => (
                        <button key={n} onClick={() => setPersonen(n)}
                          className={`flex-1 rounded-xl border py-2.5 text-sm font-bold ${personen === n ? 'text-white' : 'border-gray-300 text-gray-600'}`}
                          style={personen === n ? { backgroundColor: P1_RED, borderColor: P1_RED } : {}}>
                          {n}
                        </button>
                      ))}
                    </div>
                  </div>
                  <input value={honeypot} onChange={e => setHoneypot(e.target.value)} tabIndex={-1} autoComplete="off"
                    style={{ position: 'absolute', left: '-9999px', height: 0, width: 0, opacity: 0 }} aria-hidden="true" />
                  <button onClick={submit} disabled={submitting}
                    className="w-full text-white font-bold rounded-xl py-3.5 text-base disabled:opacity-60"
                    style={{ backgroundColor: P1_RED }} data-testid="event-submit">
                    {submitting ? 'Wird gesendet…' : 'Auf die Gästeliste ✓'}
                  </button>
                  <p className="text-[11px] text-gray-400 text-center">
                    Wir nutzen deine Daten nur für die Gästeliste und um dich rund ums Event zu kontaktieren.
                  </p>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
